import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
var __jsx = React.createElement;
import React, { useState, useMemo, useEffect, useRef } from "react";
import Modal from "../../common/Modal";
import ReportDetails from "./ReportDetails";
import SubmissionFormContent from "./SubmissionFormContent";
import Loader from "../../Loader";
import useGetSubmissionDetail from "../services/useGetSubmissionDetail";
import useGetFieldConfigs from "../services/useGetFieldConfigs";
import useGetWorkspaceConfig from "../services/useGetWorkspaceConfig";
import usePopulateSubmission from "../services/usePopulateSubmission";
import UnsavedChangesModal from "./UnsavedChangesModal";
import SubmissionFormFooter from "./SubmissionFormFooter";
import ACRStore from "../../../utils/ACRStore";
import { useStoreState } from "pullstate";
import { convertFieldConfigsToSchemas } from "../../../utils/acrUtils";
import useSubmissionActions from "../hooks/useSubmissionActions";
import SubmissionStatusBadge from "../AllACRSubmissions/SubmissionStatusBadge";
import { useQueryClient } from "react-query";
import AwesomeIcon from "../../AwesomeIcon";
import { faMinus, faClose } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { removeFromMinimized, discardSubmissionChanges } from "../../../utils/acrStoreUtils";
import { clearSubmissionErrors } from "../../../utils/submissionErrorUtils";
import { SharedFormDataProvider } from "./SharedFormDataContext";
import AIRAFormFillingLoader from "./AIRAFormFillingLoader";
import { useErrorManager } from "../hooks/useErrorManager";
import { mergeManualRules } from "../utils/manualConditionalRules";
var ACRSubmissionForm = function ACRSubmissionForm(_ref) {
  var _submissionDetail$rep2;
  var isOpen = _ref.isOpen,
    submissionID = _ref.submissionID,
    handleClose = _ref.handleClose,
    patientId = _ref.patientId,
    workspaceId = _ref.workspaceId,
    _ref$reopenTrigger = _ref.reopenTrigger,
    reopenTrigger = _ref$reopenTrigger === void 0 ? 0 : _ref$reopenTrigger,
    onActiveSubmissionSync = _ref.onActiveSubmissionSync;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(""),
    searchQuery = _useState[0],
    setSearchQuery = _useState[1];
  var _useState2 = useState(false),
    showUnsavedWarning = _useState2[0],
    setShowUnsavedWarning = _useState2[1];
  var _useState3 = useState(submissionID),
    activeSubmissionID = _useState3[0],
    setActiveSubmissionID = _useState3[1];
  var _useState4 = useState(false),
    hasFormRendered = _useState4[0],
    setHasFormRendered = _useState4[1];
  var _useState5 = useState(false),
    showPopulateOverlay = _useState5[0],
    setShowPopulateOverlay = _useState5[1];
  var queryClient = useQueryClient();
  var prevSubmissionIDRef = useRef(null);
  var prevPatientIdRef = useRef(null);
  var prevIsOpenRef = useRef(isOpen);
  var prevReopenTriggerRef = useRef(reopenTrigger);

  // Check if this submission is minimized in the store
  var isMinimized = useStoreState(ACRStore, function (s) {
    var minimizedList = s.minimizedSubmissions[patientId] || [];
    return minimizedList.some(function (m) {
      return m.submissionId === activeSubmissionID;
    });
  }, [patientId, activeSubmissionID]);
  var _useGetSubmissionDeta = useGetSubmissionDetail({
      submissionId: activeSubmissionID,
      workspaceId: workspaceId,
      enabled: !isMinimized
    }),
    submissionDetailFromAPI = _useGetSubmissionDeta.data,
    isLoadingSubmission = _useGetSubmissionDeta.isLoading;

  // Populate submission mutation
  var populateMutation = usePopulateSubmission();

  // Use cached submissionDetail from store if API query is disabled
  var cachedSubmissionDetail = useStoreState(ACRStore, function (s) {
    return activeSubmissionID ? s.submissionDetails[activeSubmissionID] : undefined;
  }, [activeSubmissionID]);
  var submissionDetail = submissionDetailFromAPI || cachedSubmissionDetail;
  var _useGetFieldConfigs = useGetFieldConfigs({
      workspaceId: workspaceId,
      enabled: !!workspaceId,
      excludeHidden: true
    }),
    fieldConfigsData = _useGetFieldConfigs.data,
    isLoadingFields = _useGetFieldConfigs.isLoading;

  // Extract fields, cross-field rules, and registry tagged fields from the response
  var fieldConfigs = useMemo(function () {
    return (fieldConfigsData === null || fieldConfigsData === void 0 ? void 0 : fieldConfigsData.fields) || [];
  }, [fieldConfigsData]);
  var crossFieldRules = useMemo(function () {
    return (fieldConfigsData === null || fieldConfigsData === void 0 ? void 0 : fieldConfigsData.crossFieldRules) || [];
  }, [fieldConfigsData]);
  var registryTaggedFields = useMemo(function () {
    return (fieldConfigsData === null || fieldConfigsData === void 0 ? void 0 : fieldConfigsData.registryTaggedFields) || {};
  }, [fieldConfigsData]);

  // Fetch workspace config to populate login status
  useGetWorkspaceConfig({
    workspaceId: workspaceId,
    enabled: !!workspaceId && isOpen
  });
  var isLoading = isLoadingSubmission || isLoadingFields;

  // Handle reopening same submission while minimized via reopenTrigger
  useEffect(function () {
    var triggerChanged = reopenTrigger !== prevReopenTriggerRef.current;
    if (triggerChanged && reopenTrigger > 0 && isMinimized && activeSubmissionID) {
      // Remove from minimized list
      removeFromMinimized(patientId, activeSubmissionID);
    }
    prevReopenTriggerRef.current = reopenTrigger;
  }, [reopenTrigger, isMinimized, activeSubmissionID, patientId]);

  // Handle reopening same submission while minimized via isOpen toggle
  useEffect(function () {
    var wasJustOpened = isOpen && !prevIsOpenRef.current;
    if (wasJustOpened && isMinimized && activeSubmissionID) {
      // Remove from minimized list
      removeFromMinimized(patientId, activeSubmissionID);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, isMinimized, activeSubmissionID, patientId]);

  // Handle submission switching - allow switching freely with minimize support
  useEffect(function () {
    var isDifferentSubmission = submissionID !== null && activeSubmissionID !== null && activeSubmissionID !== submissionID;
    if (isDifferentSubmission) {
      // Switch to new submission (old one can be minimized if needed)
      setActiveSubmissionID(submissionID);
      // Remove new submission from minimized list if it's there
      if (submissionID) {
        removeFromMinimized(patientId, submissionID);
      }
    } else if (submissionID !== activeSubmissionID) {
      // First time opening or submissionID changed to null
      setActiveSubmissionID(submissionID);
      // Remove from minimized list if it's there
      if (submissionID) {
        removeFromMinimized(patientId, submissionID);
      }
    }
    prevSubmissionIDRef.current = submissionID;
    prevPatientIdRef.current = (submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id) || null;
  }, [submissionID, activeSubmissionID, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id, patientId]);

  // Clear minimized state when the form is open with a submission
  useEffect(function () {
    if (isOpen && activeSubmissionID) {
      // Remove from minimized list if present
      removeFromMinimized(patientId, activeSubmissionID);
    }
  }, [isOpen, activeSubmissionID, patientId]);

  // Convert field configs to schemas and merge manual conditional rules
  var fieldSchemas = useMemo(function () {
    if (!fieldConfigs || fieldConfigs.length === 0) return [];
    var schemas = convertFieldConfigsToSchemas(fieldConfigs);
    return mergeManualRules(schemas);
  }, [fieldConfigs]);
  var formData = useStoreState(ACRStore, function (s) {
    var _s$submissionFormData;
    return (_s$submissionFormData = s.submissionFormData[(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id) || ""]) === null || _s$submissionFormData === void 0 ? void 0 : _s$submissionFormData[activeSubmissionID || 0];
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id, activeSubmissionID]);

  // Centralized error manager - fetches, computes, and merges all errors
  var _useErrorManager = useErrorManager({
      activeSubmissionID: activeSubmissionID,
      submissionID: submissionID,
      acrValidationErrors: submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.acr_validation_errors,
      submissionDetail: submissionDetail,
      formData: formData,
      fieldSchemas: fieldSchemas,
      crossFieldRules: crossFieldRules,
      registryTaggedFields: registryTaggedFields
    }),
    fieldErrorsList = _useErrorManager.fieldErrorsList,
    fieldErrors = _useErrorManager.fieldErrors,
    allFullFieldErrors = _useErrorManager.allFullFieldErrors,
    errorsForBadge = _useErrorManager.errorsForBadge,
    errorsForSubmit = _useErrorManager.errorsForSubmit,
    serverFieldErrors = _useErrorManager.serverFieldErrors,
    validationFieldErrors = _useErrorManager.validationFieldErrors,
    allRequiredFieldsCompleted = _useErrorManager.allRequiredFieldsCompleted,
    errorManagerChangedFields = _useErrorManager.changedFieldsSet;
  var _useSubmissionActions = useSubmissionActions(activeSubmissionID, submissionDetail, workspaceId, function () {}, queryClient, patientId, function () {}),
    handleSubmit = _useSubmissionActions.handleSubmit,
    handleSave = _useSubmissionActions.handleSave,
    isSubmitting = _useSubmissionActions.isSubmitting,
    isSaving = _useSubmissionActions.isSaving,
    changedFieldsSet = _useSubmissionActions.changedFieldsSet;

  // Sync validation errors from backend to originalErrorFields
  useEffect(function () {
    if (submissionID && validationFieldErrors && Object.keys(validationFieldErrors).length > 0) {
      ACRStore.update(function (store) {
        store.originalErrorFields[submissionID] = new Set(Object.keys(validationFieldErrors));
      });
    }
  }, [submissionID, validationFieldErrors, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.acr_validation_errors]);

  // Track when form has rendered (to prevent showing overlay before form is visible)
  useEffect(function () {
    if (!isLoading && fieldSchemas && fieldSchemas.length > 0 && submissionDetail) {
      // Set flag after a brief delay to ensure form content is rendered
      var timer = setTimeout(function () {
        setHasFormRendered(true);
      }, 100);
      return function () {
        return clearTimeout(timer);
      };
    } else {
      setHasFormRendered(false);
    }
  }, [isLoading, fieldSchemas, submissionDetail]);

  // Reset hasFormRendered when submission changes
  useEffect(function () {
    setHasFormRendered(false);
  }, [activeSubmissionID]);

  // Track populate mutation state - show message once populate starts, hide after success or error
  useEffect(function () {
    if (populateMutation !== null && populateMutation !== void 0 && populateMutation.isLoading) {
      setShowPopulateOverlay(true);
    } else if (populateMutation !== null && populateMutation !== void 0 && populateMutation.isSuccess || populateMutation !== null && populateMutation !== void 0 && populateMutation.isError) {
      // Clear overlay after success (data applied) or error
      setShowPopulateOverlay(false);
    }
  }, [populateMutation === null || populateMutation === void 0 ? void 0 : populateMutation.isLoading, populateMutation === null || populateMutation === void 0 ? void 0 : populateMutation.isSuccess, populateMutation === null || populateMutation === void 0 ? void 0 : populateMutation.isError]);

  // Handle Populate button click
  var handlePopulate = function handlePopulate() {
    if (activeSubmissionID && workspaceId) {
      populateMutation === null || populateMutation === void 0 || populateMutation.mutate({
        submissionId: activeSubmissionID,
        workspaceId: workspaceId
      });
    }
  };
  var handleMinimize = function handleMinimize() {
    if (!activeSubmissionID || !submissionDetail) return;

    // Add to minimized list in store
    ACRStore.update(function (store) {
      var minimizedList = store.minimizedSubmissions[patientId] || [];

      // Check if already minimized
      var alreadyMinimized = minimizedList.some(function (m) {
        return m.submissionId === activeSubmissionID;
      });
      if (!alreadyMinimized) {
        store.minimizedSubmissions[patientId] = [].concat(_toConsumableArray(minimizedList), [{
          submissionId: activeSubmissionID,
          workspaceId: workspaceId,
          report: submissionDetail.report || undefined,
          status: submissionDetail.status,
          acrStatus: submissionDetail.acr_status
        }]);
      }
    });
    handleClose();
  };
  var handleMaximize = function handleMaximize() {
    if (!activeSubmissionID) return;

    // Remove from minimized list
    ACRStore.update(function (store) {
      var minimizedList = store.minimizedSubmissions[patientId] || [];
      store.minimizedSubmissions[patientId] = minimizedList.filter(function (m) {
        return m.submissionId !== activeSubmissionID;
      });
    });
  };
  var handleModalClose = function handleModalClose() {
    if (changedFieldsSet && changedFieldsSet.size > 0) {
      setShowUnsavedWarning(true);
    } else {
      handleClose();
    }
  };
  var handleDiscardChanges = function handleDiscardChanges() {
    // Revert changed fields to original values from stored submission detail
    if (submissionDetail && activeSubmissionID && changedFieldsSet) {
      ACRStore.update(function (store) {
        var _store$submissionOrig, _store$submissionChan;
        var patientId = submissionDetail.patient_id;
        var originalFieldData = (_store$submissionOrig = store.submissionOriginalFieldData[patientId]) === null || _store$submissionOrig === void 0 ? void 0 : _store$submissionOrig[activeSubmissionID];

        // Revert each changed field to its original value
        changedFieldsSet.forEach(function (fieldName) {
          var _store$submissionForm;
          if ((_store$submissionForm = store.submissionFormData[patientId]) !== null && _store$submissionForm !== void 0 && _store$submissionForm[activeSubmissionID] && originalFieldData) {
            var originalValue = originalFieldData[fieldName];
            store.submissionFormData[patientId][activeSubmissionID][fieldName] = originalValue;
          }
        });
        if ((_store$submissionChan = store.submissionChangedFields[patientId]) !== null && _store$submissionChan !== void 0 && _store$submissionChan[activeSubmissionID]) {
          store.submissionChangedFields[patientId][activeSubmissionID] = new Set();
        }
      });

      // Clear all errors when discarding changes
      clearSubmissionErrors(activeSubmissionID);

      // Clear errors for this submission
      discardSubmissionChanges(submissionDetail.patient_id, activeSubmissionID);
    }
    setShowUnsavedWarning(false);
    handleClose();
  };
  var handleCancelUnsavedWarning = function handleCancelUnsavedWarning() {
    setShowUnsavedWarning(false);
  };

  // Create shared context value for read-only data (memoized)
  var sharedFormDataValue = useMemo(function () {
    return {
      submissionDetail: submissionDetail || {},
      formData: formData || {},
      fieldSchemas: fieldSchemas,
      fullFieldErrors: allFullFieldErrors,
      fieldErrorsList: fieldErrorsList,
      // From error manager (merged, stale-filtered)
      errorsForBadge: errorsForBadge,
      // From error manager (blocking errors only)
      errorsForSubmit: errorsForSubmit,
      // From error manager (blocking errors only)
      serverFieldErrors: serverFieldErrors || {},
      validationFieldErrors: validationFieldErrors || {},
      allRequiredFieldsCompleted: allRequiredFieldsCompleted,
      // From error manager (checks all required types)
      changedFieldsSet: errorManagerChangedFields
    };
  }, [submissionDetail, formData, fieldSchemas, allFullFieldErrors, fieldErrorsList, errorsForBadge, errorsForSubmit, allRequiredFieldsCompleted, serverFieldErrors, validationFieldErrors, errorManagerChangedFields]);

  // Memoized modal title for performance
  var modalTitle = useMemo(function () {
    var _submissionDetail$rep;
    return /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col items-start"
    }, /*#__PURE__*/React.createElement("div", {
      className: ""
    }, t("label.acr.acr_submission_form")), /*#__PURE__*/React.createElement("div", {
      className: "text-base text-gray-700 dark:text-gray-300 flex items-center gap-3"
    }, (submissionDetail === null || submissionDetail === void 0 || (_submissionDetail$rep = submissionDetail.report) === null || _submissionDetail$rep === void 0 ? void 0 : _submissionDetail$rep.report_heading) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "font-semibold"
    }, submissionDetail.report.report_heading), /*#__PURE__*/React.createElement("span", {
      className: "text-gray-600 dark:text-gray-500"
    }, "|")), /*#__PURE__*/React.createElement("span", null, t("label.acr.exam_id"), ": ", submissionID), (submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.status) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "text-gray-600 dark:text-gray-500"
    }, "|"), /*#__PURE__*/React.createElement(SubmissionStatusBadge, {
      status: submissionDetail.status,
      acrStatus: submissionDetail.acr_status,
      validationErrors: submissionDetail.acr_validation_errors
    }))));
  }, [t, submissionID, submissionDetail === null || submissionDetail === void 0 || (_submissionDetail$rep2 = submissionDetail.report) === null || _submissionDetail$rep2 === void 0 ? void 0 : _submissionDetail$rep2.report_heading, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.status, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.acr_status, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.acr_validation_errors]);
  if (!submissionID) {
    return /*#__PURE__*/React.createElement(Modal, {
      show: isOpen,
      onClose: handleModalClose,
      title: "ACR Submission Form",
      className: "w-full sm:w-12/12 md:w-11/12 lg:w-10/12 xl:w-9/12 h-[90vh] max-h-[900px] flex flex-col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center justify-center h-full text-gray-600 dark:text-gray-400"
    }, /*#__PURE__*/React.createElement("p", null, t("label.no_submission_data_found"))));
  }
  if (isLoading || !submissionDetail) {
    return /*#__PURE__*/React.createElement(Modal, {
      show: isOpen,
      onClose: handleModalClose,
      title: /*#__PURE__*/React.createElement("div", {
        className: "flex flex-col items-start"
      }, /*#__PURE__*/React.createElement("div", {
        className: ""
      }, t("label.acr.acr_submission_form")), /*#__PURE__*/React.createElement("div", {
        className: "text-base text-gray-700 dark:text-gray-300"
      }, t("label.acr.exam_id"), " : ", submissionID)),
      className: "w-full sm:w-12/12 md:w-11/12 lg:w-10/12 xl:w-9/12 h-[90vh] max-h-[900px] flex flex-col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center justify-center h-full"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "large"
    })));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    show: isOpen,
    onClose: handleModalClose,
    title: modalTitle,
    className: "w-full bg-white dark:bg-gray-900 sm:w-12/12 md:w-11/12 lg:w-10/12 xl:w-9/12 h-[90vh] max-h-[1000px] flex flex-col",
    closeButton: /*#__PURE__*/React.createElement("div", {
      className: "flex items-center gap-2"
    }, /*#__PURE__*/React.createElement("button", {
      onClick: handleMinimize,
      className: "p-2 hover:scale-125 transition-all",
      "aria-label": "Minimize",
      title: t("button.minimize")
    }, /*#__PURE__*/React.createElement(AwesomeIcon, {
      name: faMinus,
      className: "w-6 h-6"
    })), /*#__PURE__*/React.createElement("button", {
      onClick: handleModalClose,
      className: "p-2",
      "aria-label": "Close",
      title: t("button.close")
    }, /*#__PURE__*/React.createElement(AwesomeIcon, {
      name: faClose,
      className: "w-6 h-6"
    })))
  }, /*#__PURE__*/React.createElement("div", {
    className: "acr-submission-form flex flex-1 min-h-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-1/3 flex-shrink-0"
  }, /*#__PURE__*/React.createElement(ReportDetails, {
    submissionDetail: submissionDetail
  })), /*#__PURE__*/React.createElement(SharedFormDataProvider, {
    value: sharedFormDataValue
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-1 flex flex-col min-w-0 relative"
  }, /*#__PURE__*/React.createElement(AIRAFormFillingLoader, {
    show: hasFormRendered && showPopulateOverlay
  }), /*#__PURE__*/React.createElement(SubmissionFormContent, {
    searchQuery: searchQuery,
    setSearchQuery: setSearchQuery,
    submissionDetail: submissionDetail,
    fieldErrors: fieldErrors,
    fullFieldErrors: allFullFieldErrors,
    fieldErrorsList: fieldErrorsList,
    isLoading: isSaving || isSubmitting,
    fieldSchemas: fieldSchemas,
    workspaceId: workspaceId
  }), /*#__PURE__*/React.createElement(SubmissionFormFooter, {
    workspaceId: workspaceId,
    populateMutation: populateMutation,
    hasFormRendered: hasFormRendered,
    showPopulateOverlay: showPopulateOverlay,
    isSaving: isSaving,
    isSubmitting: isSubmitting,
    onSave: handleSave,
    onSubmit: handleSubmit,
    onPopulate: handlePopulate
  }))))), /*#__PURE__*/React.createElement(UnsavedChangesModal, {
    show: showUnsavedWarning,
    title: t("label.unsaved_changes"),
    message: t("label.acr.unsaved_warning_text"),
    confirmLabel: t("button.discard"),
    onConfirm: handleDiscardChanges,
    onCancel: handleCancelUnsavedWarning,
    closeOnClickAway: true
  }));
};
export default ACRSubmissionForm;